import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'urql';
import { Q_CONTENT_ITEMS, M_SET_CONTENT_TRIAGE, M_SET_CONTENT_TRIAGE_BULK } from '@/lib/queries';

/**
 * Review queue — incoming feed items ranked by AI relevance. Keep what's worth
 * posting; dismiss the rest. The queue IS the set of `pending` content items
 * from the API; keep/dismiss persist via the triage mutations.
 */

/** Live content item as returned by Q_CONTENT_ITEMS. */
interface ContentItem {
  id: string;
  sourceId: string;
  sourceLabel: string;
  sourceKind: string;
  title: string;
  url: string | null;
  summary: string | null;
  used: boolean;
  postCount: number;
  relevance: number;
  triageStatus: string;
  publishedAt: string | null;
  createdAt: string;
}

const kindMeta: Record<string, string> = { rss: '📰', pdf: '▤', link: '🔗', scrape: '◰', keyword: '🔎' };

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function ReviewRoute() {
  const nav = useNavigate();

  const [itemsQ, refetch] = useQuery<{ contentItems: ContentItem[] }>({
    query: Q_CONTENT_ITEMS,
    variables: { triageStatus: 'pending', orderBy: 'relevance', limit: 200 },
  });
  const [, setTriage] = useMutation(M_SET_CONTENT_TRIAGE);
  const [, setTriageBulk] = useMutation(M_SET_CONTENT_TRIAGE_BULK);

  const [sel, setSel] = useState<string[]>([]);

  const pending = useMemo(() => itemsQ.data?.contentItems ?? [], [itemsQ.data]);
  const digest = useMemo(() => pending.filter((q) => q.relevance >= 70).slice(0, 3), [pending]);

  const reload = () => refetch({ requestPolicy: 'network-only' });

  const keep = async (id: string) => {
    await setTriage({ id, status: 'kept' });
    setSel((s) => s.filter((x) => x !== id));
    reload();
  };
  const dismiss = async (id: string) => {
    await setTriage({ id, status: 'dismissed' });
    setSel((s) => s.filter((x) => x !== id));
    reload();
  };
  const toggle = (id: string) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const keepSel = async () => {
    if (sel.length === 0) return;
    await setTriageBulk({ ids: sel, status: 'kept' });
    setSel([]);
    reload();
  };
  const dismissSel = async () => {
    if (sel.length === 0) return;
    await setTriageBulk({ ids: sel, status: 'dismissed' });
    setSel([]);
    reload();
  };

  const createPosts = (item: ContentItem) =>
    nav('/app/create', {
      state: { prefill: { contentItemId: item.id, title: item.title, summary: item.summary, url: item.url } },
    });

  const allSel = pending.length > 0 && pending.every((q) => sel.includes(q.id));

  return (
    <div className="p-8 space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-stone-900">Review queue</h1>
        <p className="text-sm text-stone-500 mt-1">Incoming items from your feeds, ranked by AI relevance. Keep what's worth posting; the rest stays out of your library.</p>
      </div>

      {itemsQ.fetching && pending.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-xl p-12 text-center text-sm text-stone-400">Loading review queue…</div>
      ) : (
        <>
          {digest.length > 0 && (
            <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-stone-900 flex items-center gap-2"><span className="text-violet-600">✨</span> Today's digest — {digest.length} worth posting about</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {digest.map((q) => (
                  <button key={q.id} onClick={() => createPosts(q)} className="flex items-center gap-2 px-3 py-2 bg-white border border-violet-200 rounded-lg hover:border-violet-400 text-left">
                    <span className="text-lg">{kindMeta[q.sourceKind] ?? '📰'}</span>
                    <span className="min-w-0"><span className="block text-sm font-medium text-stone-900 truncate max-w-[220px]">{q.title}</span><span className="block text-[11px] text-violet-600 font-semibold">{q.relevance}% relevant · Create posts →</span></span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {pending.length > 0 && (
            <div className="flex items-center justify-between bg-white border border-stone-200 rounded-xl px-4 py-2.5">
              <label className="flex items-center gap-2.5 text-sm text-stone-600 cursor-pointer select-none">
                <input type="checkbox" checked={allSel} onChange={() => setSel(allSel ? [] : pending.map((q) => q.id))} className="accent-violet-600 w-4 h-4" />
                {sel.length > 0 ? `${sel.length} selected` : `${pending.length} to review`}
              </label>
              {sel.length > 0 && (
                <div className="flex gap-2">
                  <button onClick={keepSel} className="px-3 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-xs font-medium">Keep selected</button>
                  <button onClick={dismissSel} className="px-3 py-1.5 border border-stone-200 text-stone-600 rounded-lg hover:bg-stone-50 text-xs font-medium">Dismiss selected</button>
                </div>
              )}
            </div>
          )}

          {pending.length === 0 ? (
            <div className="bg-white border border-dashed border-stone-300 rounded-xl p-12 text-center">
              <p className="text-4xl mb-2">🎉</p><p className="font-semibold text-stone-700">Inbox zero</p><p className="text-sm text-stone-400">Nothing left to review. New items will appear here as your feeds fetch.</p>
              <button onClick={() => nav('/app/content')} className="mt-3 text-sm text-violet-600 font-medium">View Content library →</button>
            </div>
          ) : (
            <div className="space-y-3">
              {pending.map((q) => {
                const isSel = sel.includes(q.id);
                return (
                  <div key={q.id} className={`bg-white border rounded-xl p-4 flex gap-4 transition-colors ${isSel ? 'border-violet-400 bg-violet-50/30' : 'border-stone-200 hover:border-stone-300'}`}>
                    <input type="checkbox" checked={isSel} onChange={() => toggle(q.id)} className="accent-violet-600 w-4 h-4 mt-1 flex-shrink-0" />
                    <div className="w-14 h-14 rounded-lg flex items-center justify-center text-2xl flex-shrink-0 bg-stone-100">{kindMeta[q.sourceKind] ?? '📰'}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-semibold">NEW</span>
                      </div>
                      <p className="font-semibold text-stone-900 truncate">{q.title}</p>
                      <p className="text-sm text-stone-500 line-clamp-2 mt-0.5">{q.summary}</p>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className="text-xs text-stone-400">{q.sourceLabel} · {relativeTime(q.publishedAt ?? q.createdAt)}</span>
                        <span className="flex items-center gap-1.5"><span className="w-16 h-1.5 bg-stone-100 rounded-full overflow-hidden"><span className={`block h-full rounded-full ${q.relevance >= 80 ? 'bg-emerald-500' : q.relevance >= 60 ? 'bg-amber-500' : 'bg-stone-400'}`} style={{ width: `${q.relevance}%` }} /></span><span className="text-[11px] text-stone-500 font-medium">{q.relevance}% relevant</span></span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      <button onClick={() => createPosts(q)} className="px-3 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-xs font-medium whitespace-nowrap">Create posts</button>
                      <button onClick={() => keep(q.id)} className="px-3 py-1.5 border border-stone-200 text-stone-700 rounded-lg hover:bg-stone-50 text-xs font-medium">Keep</button>
                      <button onClick={() => dismiss(q.id)} className="px-3 py-1.5 text-stone-400 hover:text-red-500 text-xs">Dismiss</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
